import { useEffect, useState } from "react";
import { Button } from "@bht/ui";

/**
 * "Add to home screen" affordance.
 *
 * WHY THIS EXISTS. The only piece of free-text feedback production has ever
 * received asked "Is there an app for this? Or how to add this to my phone's
 * homescreen". The manifest has always been correct and the app has always been
 * installable; nothing anywhere told a student so.
 *
 * Two paths, because the platforms differ. Chromium fires beforeinstallprompt
 * and hands us a prompt we can trigger from a button. iOS Safari does not
 * implement it at all and never will, so there the only honest answer is to
 * describe the Share menu. Rendering nothing when already installed keeps the
 * drawer from offering something the user has done.
 */

type InstallEvent = Event & { prompt: () => Promise<void> };

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS reports installed state here rather than through display-mode.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function InstallApp() {
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Chromium shows its own mini-infobar unless this is prevented, and we
      // want the button in the drawer to be the single place this is offered.
      e.preventDefault();
      setDeferred(e as InstallEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  if (deferred) {
    return (
      <Button
        variant="ghost"
        full
        onClick={() => {
          void deferred.prompt();
          setDeferred(null);
        }}
      >
        Add to home screen
      </Button>
    );
  }

  // No prompt event. On iOS that is expected and we can give real instructions.
  // On anything else the browser has decided the app is not installable yet, and
  // inventing steps we cannot verify would be worse than staying quiet.
  if (!isIos()) return null;

  return (
    <div>
      <Button variant="ghost" full onClick={() => setShowIosHelp((v) => !v)}>
        Add to home screen
      </Button>
      {showIosHelp && (
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            lineHeight: 1.5,
            color: "var(--fg-2)",
            margin: "8px 2px 0",
          }}
        >
          Tap the Share button at the bottom of Safari, then choose Add to Home
          Screen. It opens full screen after that, and your work is saved the same
          way either way.
        </p>
      )}
    </div>
  );
}
